import React, { useCallback, useEffect, useRef, useState } from "react";

import { ArtifactStyle } from "../../../verification/document/artifactResponse/VerficationArtifactsStyles";
import { useVerification } from "../../../../../context/Verification.context";
import EcheckVideoPlayer, {
  normalizePlaybackUrl,
  getWalkthroughEmbed,
} from "../../../../../utils/videoPlayer";

/**
 * Video artifact player — uses the shared white-label player or iframe for YouTube/Vimeo
 * and preserves watch-time tracking into verification.artifactsResponse.videoResponse.
 */
const VideoPlayer = (props) => {
  const {
    artifact,
    setVideoScreen,
    selectedUrl: selectedUrlProp,
    setIsLoading,
    setAnimateIn,
    onVideoEnded,
  } = props;
  const verification = useVerification();

  const [totalDuration, setTotalDuration] = useState(0);
  const [watchedTime, setWatchedTime] = useState(0);
  const [lastWatchedTime, setLastWatchedTime] = useState(0);
  const lastTrackedRef = useRef(0);

  const languageUrl = !verification?.aiData?.spanish
    ? artifact?.url
    : artifact?.es_url;

  const rawUrl = selectedUrlProp || languageUrl || null;
  const embed = getWalkthroughEmbed(rawUrl);
  const selectedUrl = normalizePlaybackUrl(rawUrl);

  const handleTimeUpdate = useCallback((currentTime, duration) => {
    if (duration && isFinite(duration)) {
      setTotalDuration(duration);
    }
    setWatchedTime(currentTime);
    setLastWatchedTime((prevLast) => {
      if (currentTime > prevLast + 1.5) {
        // large jump — treat as skip; still update last position
      }
      return currentTime;
    });
  }, []);

  const handleReady = useCallback(() => {
    setIsLoading?.(false);
    setAnimateIn?.(true);
  }, [setIsLoading, setAnimateIn]);

  const handleDuration = useCallback((d) => {
    if (d && isFinite(d)) setTotalDuration(d);
  }, []);

  const handleEnded = useCallback(() => {
    onVideoEnded?.();
  }, [onVideoEnded]);

  useEffect(() => {
    if (!watchedTime) return;
    // Throttle artifact response updates (~1s)
    if (Math.abs(watchedTime - lastTrackedRef.current) < 0.9) return;
    lastTrackedRef.current = watchedTime;

    verification.setArtifactsResponse((prevResponse) => {
      const newVideoResponse = {
        user_id: parseInt(verification?.customer?.userId),
        video_id: artifact?.id,
        total_secs: parseInt(totalDuration) || 0,
        watched_secs: parseInt(watchedTime),
        last_watched_secs: parseInt(lastWatchedTime || watchedTime),
        created_by: parseInt(verification.employeeId),
        updated_by: 0,
        isCurrent: true,
      };

      const videoResponses = prevResponse?.videoResponse || [];
      const existingResponse = videoResponses.find(
        (response) => response.video_id === newVideoResponse.video_id,
      );

      if (existingResponse) {
        const updatedVideoResponses = videoResponses.map((response) =>
          response.video_id === newVideoResponse.video_id
            ? { ...newVideoResponse, id: existingResponse?.id }
            : response,
        );
        return { ...prevResponse, videoResponse: updatedVideoResponses };
      }
      return {
        ...prevResponse,
        videoResponse: [...videoResponses, newVideoResponse],
      };
    });

    setVideoScreen?.(true);
  }, [
    watchedTime,
    totalDuration,
    lastWatchedTime,
    artifact?.id,
    verification,
    setVideoScreen,
  ]);

  if (!embed || !selectedUrl) {
    return (
      <div style={ArtifactStyle.videoBox}>
        {verification.aiData.spanish ? (
          <pre style={ArtifactStyle.textBox}>No Spanish version available</pre>
        ) : (
          <pre style={ArtifactStyle.textBox}>No English version available</pre>
        )}
      </div>
    );
  }

  if (embed.type === "youtube" || embed.type === "vimeo") {
    return (
      <div
        style={{
          ...ArtifactStyle.videoBox,
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        <iframe
          key={embed.src}
          src={embed.src}
          title={artifact?.title || artifact?.es_title || "Video Artifact"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
          onLoad={() => {
            setIsLoading?.(false);
            setAnimateIn?.(true);
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ ...ArtifactStyle.videoBox, width: "100%", height: "100%" }}>
      <EcheckVideoPlayer
        src={selectedUrl}
        artifactKey={artifact?.id}
        onTimeUpdate={handleTimeUpdate}
        onDuration={handleDuration}
        onReady={handleReady}
        onEnded={handleEnded}
        autoPlay
      />
    </div>
  );
};

export default VideoPlayer;
